/**
 * @file Controlled Drugs control-surface helpers.
 *
 * These helpers keep Phase 5 control migration setup in the Drugs domain while
 * reusing the approved shared native-field and branding utilities.
 */

import {
  AUTO_GROWING_TEXTAREA_CLASSES,
  bindNativeFieldBehavior,
  FIXED_TEXTAREA_CLASSES,
  getNativeFieldWrapperHtml,
  NATIVE_INPUT_CLASSES,
  READ_ONLY_VALUE_CLASSES,
  validateNativeFormFields,
} from "../../shared/forms/nativeFieldStyles.js";
import {
  applyBrandingVariables,
} from "../../shared/ui/brandingTokens.js";
import {
  getRegisteredControlValues,
} from "../../shared/ui/controls/controlCore.js";

export const DRUG_CONTROL_SURFACE_ATTRIBUTE = "data-drug-control-surface";

export const DRUG_MODAL_GRID_CLASSES = "grid grid-cols-1 gap-4 md:grid-cols-2";
export const DRUG_MODAL_SECTION_HEADING_CLASSES = [
  "border-b border-gray-200 pb-2 text-lg font-semibold text-gray-700",
  "dark:border-gray-600 dark:text-gray-300",
].join(" ");
export const DRUG_CONTROL_HELPER_TEXT_CLASSES = "text-sm text-gray-500 sm:text-xs";
export const DRUG_MODAL_INPUT_CLASSES = NATIVE_INPUT_CLASSES;
export const DRUG_MODAL_SELECT_CLASSES = NATIVE_INPUT_CLASSES;
export const DRUG_MODAL_TEXTAREA_CLASSES = FIXED_TEXTAREA_CLASSES;
export const DRUG_MODAL_AUTO_TEXTAREA_CLASSES = AUTO_GROWING_TEXTAREA_CLASSES;
export const DRUG_READ_ONLY_VALUE_CLASSES = READ_ONLY_VALUE_CLASSES;
export const DRUG_ICON_BUTTON_CLASSES = [
  "inline-flex h-7 w-7 items-center justify-center text-gray-400",
  "transition-colors hover:text-red-600 focus:outline-none focus-visible:text-red-600",
  "dark:hover:text-red-400 dark:focus-visible:text-red-400",
].join(" ");
export const DRUG_SECONDARY_BUTTON_CLASSES = [
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-gray-200",
  "bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors",
  "hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)]",
  "focus:outline-none focus-visible:border-[var(--dfc-brand-primary)] focus-visible:text-[var(--dfc-brand-primary)]",
  "disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");
export const DRUG_CANCEL_BUTTON_CLASSES = [
  "inline-flex h-9 items-center justify-center rounded-md bg-gray-200 px-4",
  "text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-300",
  "focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");
export const DRUG_PRIMARY_BUTTON_CLASSES = [
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-[var(--dfc-brand-primary)]",
  "bg-[var(--dfc-brand-primary)] px-4 py-2 text-sm font-semibold text-[var(--dfc-brand-primary-contrast)] transition-colors",
  "hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none focus-visible:bg-[var(--dfc-brand-primary-hover)]",
  "disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");
export const DRUG_APPROVE_BUTTON_CLASSES = [
  "inline-flex min-h-10 items-center justify-center rounded-md bg-green-600 px-4 py-2",
  "text-sm font-semibold text-white transition-colors hover:bg-green-700",
  "focus:outline-none focus-visible:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");
export const DRUG_DANGER_BUTTON_CLASSES = [
  "inline-flex min-h-10 items-center justify-center rounded-md bg-red-600 px-4 py-2",
  "text-sm font-semibold text-white transition-colors hover:bg-red-700",
  "focus:outline-none focus-visible:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");
export const DRUG_DANGER_TEXT_BUTTON_CLASSES = [
  "inline-flex min-h-10 items-center justify-center rounded-md px-2 py-2",
  "text-sm font-medium text-gray-500 transition-colors hover:text-red-600",
  "focus:outline-none focus-visible:text-red-600 disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");

/**
 * Renders a Drugs modal field wrapper with approved native-backed rhythm.
 * @param {string} label Field label.
 * @param {string} controlHtml Field control markup.
 * @param {object} [options] Wrapper options.
 * @param {boolean} [options.required=false] Whether to append required mark.
 * @param {string} [options.className] Additional wrapper classes.
 * @returns {string} Field wrapper HTML.
 */
export function getDrugNativeFieldHtml(label, controlHtml, options = {}) {
  return getNativeFieldWrapperHtml(label, controlHtml, options);
}

/**
 * Applies app branding variables and a stable marker to a Drugs control surface.
 * @param {HTMLElement | null} container Surface container.
 * @param {object} appData Runtime app data containing branding config.
 * @returns {void}
 */
export function applyDrugControlSurface(container, appData) {
  if (!container) {return;}
  applyBrandingVariables(container, appData);
  container.setAttribute(DRUG_CONTROL_SURFACE_ATTRIBUTE, "true");
}

/**
 * Binds approved native-backed field behaviour for a Drugs form surface.
 * Later Drugs sprints should call this only after confirming the target
 * workflow should use app-owned native validation instead of current prompts.
 * @param {HTMLElement | HTMLFormElement | null} scope Drugs form scope.
 * @returns {void}
 */
export function bindDrugNativeFieldSurface(scope) {
  if (!scope) {return;}
  bindNativeFieldBehavior(scope);
}

/**
 * Validates native-backed fields inside a Drugs form surface.
 * @param {HTMLElement | HTMLFormElement | null} scope Drugs form scope.
 * @returns {boolean} Whether all native-backed fields are valid.
 */
export function validateDrugNativeFields(scope) {
  if (!scope) {return true;}
  const nativeValid = validateNativeFormFields(scope);
  const registeredExtraction = getRegisteredControlValues(scope, { validate: true });
  return nativeValid && registeredExtraction.valid;
}

/**
 * Applies an action-specific required error to a native-backed Drugs field.
 * This is for controls that are optional until a specific action, such as
 * rejecting an approval or confirming a destructive audit action.
 * @param {HTMLElement | HTMLFormElement | null} scope Drugs form/modal scope.
 * @param {HTMLInputElement | HTMLTextAreaElement | null} field Field to check.
 * @param {string} message Validation message to surface.
 * @returns {boolean} Whether the field has a value.
 */
export function requireDrugActionFieldValue(scope, field, message) {
  if (!field) {return false;}
  bindDrugNativeFieldSurface(scope);

  const clearActionValidation = () => {
    if (!field.value.trim()) {return;}
    field.setCustomValidity("");
    field.removeAttribute("aria-required");
    field.dataset.nativeFieldValidationVisible = "false";
    field.dispatchEvent(new Event("change", { bubbles: true }));
  };

  if (field.dataset.drugActionValidationBound !== "true") {
    field.dataset.drugActionValidationBound = "true";
    field.addEventListener("input", clearActionValidation);
  }

  if (field.value.trim()) {
    clearActionValidation();
    return true;
  }

  field.setAttribute("aria-required", "true");
  field.setCustomValidity(message);
  validateDrugNativeFields(scope);
  return false;
}
